import { useState } from "react";
import { useForm } from "react-hook-form";
import { Can } from "@core/permissions";
import { reportError } from "@portal/common/errorMessage";
import { Icon } from "@core/icons";
import { tokens } from "@core/theme/tokens";
import { ImageWithPlaceholder } from "@core/media";
import {
  EmptyState,
  Field,
  FeedbackState,
  PortalButton,
  SidePanel,
  inputStyle,
  useToast,
} from "@portal/common";
import {
  catalogLabel,
  resolveCatalogId,
  type CatalogEntry,
  type Maintenance,
  type MaintenanceInput,
  type Vehicle,
} from "./api";
import {
  maintenanceFormSchema,
  type MaintenanceFormValues,
} from "./vehicleSchemas";
import {
  useMaintenanceMutations,
  useMaintenances,
  useVehicleCatalog,
} from "./vehicleQueries";
import { formatShortDate } from "@core/format/shortDate";

// admin.jsx no formatea el kilometraje que ya trae `MANT_DEF` (viene
// literal como `'29.840 km'`), pero sí el que ingresa el formulario
// (placeholder `32.500`): mismo separador de miles en ambos casos, así que
// acá se aplica siempre para no depender de cómo llegó el dato.
const maintenanceMileageFormatter = new Intl.NumberFormat("es-EC");
const formatMileage = (value?: number | null) =>
  value != null ? `${maintenanceMileageFormatter.format(value)} km` : "—";

// PLAN-009 §3: viaja como `codeMessages` a `reportError` (toast, tono
// error) — un código sin mapear trae el genérico con correlación.
const MAINTENANCE_ERROR_MESSAGES: Record<string, string> = {
  ACTUAL_DATE_REQUIRED:
    "Un mantenimiento realizado requiere fecha real y kilometraje",
};

// SPEC-203 §6: `LOWER_MILEAGE` es un aviso, no un bloqueo — puede haber un
// error previo de digitación. Viaja en `Maintenance.warnings`, nunca como
// error del formulario.
const warningText = (code: string) =>
  (
    ({
      LOWER_MILEAGE:
        "El kilometraje es menor que el del último mantenimiento registrado",
    }) as Record<string, string>
  )[code] ?? null;

function MaintenanceForm({
  initial,
  types,
  workshops,
  onSubmit,
  onCancel,
  submitting,
}: {
  initial?: Maintenance;
  types: CatalogEntry[];
  workshops: CatalogEntry[];
  onSubmit: (input: MaintenanceInput) => void;
  onCancel: () => void;
  submitting?: boolean;
}) {
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<MaintenanceFormValues>({
    // admin.jsx:686 `BLANK_MF`: al registrar (sin `initial`), el formulario
    // arranca con el primer tipo y el primer taller del catálogo ya
    // seleccionados, no con el placeholder vacío — solo al editar se resuelve
    // el id real desde la etiqueta guardada.
    defaultValues: {
      maintenanceTypeId: initial
        ? resolveCatalogId(types, initial.type)
        : (types[0]?.id ?? ""),
      status: initial?.status ?? "scheduled",
      estimatedDate: initial?.estimatedDate ?? "",
      actualDate: initial?.actualDate ?? "",
      mileage: initial?.mileage != null ? String(initial.mileage) : "",
      // QA-MTZ-002 ronda 3 (#12): la API no devolvía el taller, así que
      // esto resolvía "" y guardar le borraba el taller al mantenimiento.
      // Ahora llega `workshopId`; la etiqueta queda como respaldo.
      workshopId: initial
        ? (initial.workshopId ?? resolveCatalogId(workshops, initial.workshop))
        : (workshops[0]?.id ?? ""),
    },
  });
  const workshopId = watch("workshopId");
  return (
    <form
      onSubmit={handleSubmit((value) => {
        const parsed = maintenanceFormSchema.safeParse(value);
        if (!parsed.success) {
          for (const issue of parsed.error.issues)
            setError(issue.path[0] as keyof MaintenanceFormValues, {
              message: issue.message,
            });
          return;
        }
        const data = parsed.data;
        onSubmit({
          maintenanceTypeId: data.maintenanceTypeId,
          status: data.status,
          ...(data.estimatedDate ? { estimatedDate: data.estimatedDate } : {}),
          ...(data.actualDate ? { actualDate: data.actualDate } : {}),
          ...(data.mileage ? { mileage: Number(data.mileage) } : {}),
          // `workshopId` no es `nullable` en el contrato (a diferencia de
          // `colorId` en `VehicleInput`): no hay forma de mandar "sin
          // taller" salvo omitir la clave, igual que el resto de los
          // opcionales de este formulario.
          ...(data.workshopId ? { workshopId: data.workshopId } : {}),
        });
      })}
    >
      <div className="label" style={{ marginBottom: 14 }}>
        {initial ? "Editar registro" : "Nuevo registro"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field
          label="Tipo · catálogo"
          error={errors.maintenanceTypeId?.message}
        >
          <select
            aria-label="Tipo de mantenimiento"
            style={inputStyle}
            {...register("maintenanceTypeId")}
          >
            <option value="">Selecciona un tipo</option>
            {types.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {catalogLabel(entry)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Estado">
          <select
            aria-label="Estado"
            style={inputStyle}
            {...register("status")}
          >
            <option value="scheduled">Programado</option>
            <option value="completed">Realizado</option>
          </select>
        </Field>
        <Field label="Fecha estimada">
          <input
            aria-label="Fecha estimada"
            type="date"
            style={inputStyle}
            {...register("estimatedDate")}
          />
        </Field>
        <Field label="Fecha real" error={errors.actualDate?.message}>
          <input
            aria-label="Fecha real"
            type="date"
            style={inputStyle}
            {...register("actualDate")}
          />
        </Field>
        <Field label="Kilometraje" error={errors.mileage?.message}>
          <input
            aria-label="Kilometraje"
            style={inputStyle}
            placeholder="32.500"
            {...register("mileage")}
          />
        </Field>
        <Field label="Taller">
          <select
            aria-label="Taller"
            style={inputStyle}
            {...register("workshopId")}
            value={workshopId ?? ""}
          >
            <option value="">Sin especificar</option>
            {workshops.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {catalogLabel(entry)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginTop: 18,
        }}
      >
        <PortalButton size="small" disabled={submitting}>
          {submitting ? "Guardando…" : "Guardar"}
        </PortalButton>
        <button type="button" className="btn-link" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

export function MaintenancePanel({
  vehicle,
  onClose,
}: {
  vehicle: Vehicle;
  onClose: () => void;
}) {
  const maintenances = useMaintenances(vehicle.id);
  const types = useVehicleCatalog("maintenance-types");
  const workshops = useVehicleCatalog("workshops");
  const mutations = useMaintenanceMutations(vehicle.id);
  const toast = useToast();
  // SPEC-203 §4 y el prototipo solo dibujan alta de mantenimiento, nunca
  // edición por registro (el enlace "Editar" se quitó — ver
  // MaintenancePanel.test.tsx). Por eso este estado no tiene una variante
  // `Maintenance`: nada en esta pantalla vuelve a poner un registro existente
  // acá para editarlo.
  const [form, setForm] = useState<"none" | "create">("none");

  const rows = maintenances.data ?? [];
  const scheduledCount = rows.filter((m) => m.status === "scheduled").length;

  const submit = (input: MaintenanceInput) => {
    const onSuccess = (record: Maintenance) => {
      setForm("none");
      const warning = record.warnings
        ?.map((w) => warningText(w.code))
        .find((text): text is string => Boolean(text));
      toast.show(
        warning ?? "Mantenimiento registrado.",
        warning ? "neutral" : "success",
      );
    };
    mutations.create.mutate(input, {
      onSuccess,
      onError: (e) => reportError(toast, e, MAINTENANCE_ERROR_MESSAGES),
    });
  };

  // Cabecera del panel — admin.jsx:980-991 (`Sheet` anclado a la derecha,
  // 560px, `SheetHead` con antetítulo, miniatura del vehículo y metadatos).
  // Antes vivía en un `Modal` centrado (delta de contenedor SPEC-203 gate 3):
  // mismo primitivo `SidePanel` que `MembersScreen` usa para la ficha del
  // miembro (admin.jsx:670-682, mismo `SheetHead`), no una variante nueva.
  const thumb = { width: 68, height: 46, radius: 9 };
  return (
    <SidePanel
      label="Mantenimientos del vehículo"
      width={560}
      onClose={onClose}
    >
      <div
        style={{
          padding: "22px 26px 18px",
          borderBottom: `1px solid ${tokens.surface.line}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="label" style={{ marginBottom: 7 }}>
            Mantenimientos del vehículo
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <ImageWithPlaceholder
              src={vehicle.photoUrl}
              icon="car"
              iconSize={18}
              showText={false}
              imageStyle={{
                width: thumb.width,
                height: thumb.height,
                objectFit: "cover",
                borderRadius: thumb.radius,
                border: `1px solid ${tokens.surface.line}`,
                background: tokens.surface.surface2,
                flex: "none",
              }}
            />
            <div>
              <h2 className="display" style={{ fontSize: 22, margin: 0 }}>
                {vehicle.model}
              </h2>
              <div
                style={{
                  font: `400 11.5px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                  marginTop: 4,
                }}
              >
                {vehicle.plate} · {vehicle.year}
                {vehicle.color ? ` · ${vehicle.color}` : ""}
                {vehicle.sapCode ? ` · ${vehicle.sapCode}` : ""}
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="chip"
          style={{ padding: "8px 11px", flex: "none" }}
        >
          ✕
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px 28px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <span
            style={{
              font: `400 11.5px ${tokens.font.ui}`,
              color: tokens.text.tertiary,
            }}
          >
            {rows.length} registros · {scheduledCount} programado(s)
          </span>
          <Can module="members" action="canCreate">
            <PortalButton
              size="small"
              tone={form === "none" ? "primary" : "secondary"}
              onClick={() => setForm(form === "none" ? "create" : "none")}
            >
              {form === "none" ? (
                <>
                  <Icon name="plus" size={14} />
                  Registrar mantenimiento
                </>
              ) : (
                "Cerrar formulario"
              )}
            </PortalButton>
          </Can>
        </div>
        {form === "create" ? (
          <div
            style={{
              marginBottom: 18,
              padding: 18,
              background: tokens.surface.surface,
              border: `1px solid ${tokens.surface.line2}`,
              borderRadius: tokens.radius.sm,
            }}
          >
            <MaintenanceForm
              types={types.data ?? []}
              workshops={workshops.data ?? []}
              onSubmit={submit}
              onCancel={() => setForm("none")}
              submitting={mutations.create.isPending}
            />
          </div>
        ) : null}
        {maintenances.isLoading ? (
          <FeedbackState kind="loading" />
        ) : maintenances.isError ? (
          <EmptyState kind="error" />
        ) : rows.length ? (
          rows.map((m, index) => (
            <div key={m.id} style={{ display: "flex", gap: 14 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: 12,
                  flex: "none",
                  paddingTop: 20,
                }}
              >
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    flex: "none",
                    background:
                      m.status === "completed"
                        ? tokens.gold.base
                        : tokens.silver.flat,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    width: 1,
                    background:
                      index === rows.length - 1
                        ? "transparent"
                        : tokens.surface.line,
                  }}
                />
              </div>
              <div
                style={{
                  flex: 1,
                  marginBottom: 12,
                  padding: 16,
                  background: tokens.surface.surface,
                  border: `1px solid ${tokens.surface.line}`,
                  borderRadius: tokens.radius.sm,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      font: `600 14px ${tokens.font.ui}`,
                      color: tokens.text.primary,
                    }}
                  >
                    {m.type}
                  </span>
                  <span
                    className={`stat stat-${m.status === "completed" ? "gold" : "silver"}`}
                  >
                    {m.status === "completed" ? "Realizado" : "Programado"}
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px 16px",
                    marginTop: 13,
                  }}
                >
                  {(
                    [
                      ["Fecha estimada", formatShortDate(m.estimatedDate)],
                      ["Fecha real", formatShortDate(m.actualDate)],
                      ["Kilometraje", formatMileage(m.mileage)],
                      ["Taller", m.workshop ?? "—"],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label}>
                      <div
                        className="label"
                        style={{ fontSize: 9.5, marginBottom: 3 }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          font: `400 12px ${tokens.font.ui}`,
                          color: tokens.text.secondary,
                        }}
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        ) : (
          <EmptyState kind="no-data" />
        )}
      </div>
    </SidePanel>
  );
}
